import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import ArrowBackOutlined from "@mui/icons-material/ArrowBackOutlined";
import ControlPointOutlined from "@mui/icons-material/ControlPointOutlined";
import { LinearProgress, Alert, Box, Button, Stack, Typography, Divider } from "@mui/material";
import type { FC } from "react";
import { useState, Suspense, lazy } from "react";
import { EmailDomainConfigurationTable } from "../../modules/channels/email/components/email-domain/email-domain-table";
import { SupabaseCustomEmailDomainService } from "../../modules/channels/email/server/supabase-custom-email-domain-service";
import { useAccountLimitsContext } from "../../modules/context/account-limit-context";
import { usePermissions } from "../../server/auth/use-permissions";
import { useSupabaseLoader } from "../../server/supabase/hooks";
import { useDialog } from "../../utils/hooks/use-dialog";
import { SupabaseCustomEmailHeaderService } from "../../modules/channels/email/server/supabase-custom-email-header-service";
import { UpsertHeaderDialog } from "../../modules/channels/email/components/email-header/upsert-header-dialog";
import { EmailHeaderTable } from "../../modules/channels/email/components/email-header/email-header-table";

// The MIME encoding is HUGE
const EmailConfigurationTable = lazy(
  () => import("../../modules/channels/email/components/email-address/email-configuration-table"),
);

export const Email: FC = () => {
  const [showAdvancedView, setShowAdvancedView] = useState(false);

  return showAdvancedView ? (
    <AdvancedEmailConfiguration setShowAdvancedView={setShowAdvancedView} />
  ) : (
    <SimpleEmailConfiguration setShowAdvancedView={setShowAdvancedView} />
  );
};

const AdvancedEmailConfiguration: FC<{ setShowAdvancedView: (s: boolean) => void }> = ({
  setShowAdvancedView,
}) => {
  const customHeaderDialog = useDialog();

  const { data, loading, error, reload } = useSupabaseLoader(async ({ supabase, account_id }) => {
    const supabaseHeaderService = new SupabaseCustomEmailHeaderService(supabase);
    const resp = await supabaseHeaderService.getAll(account_id, {
      order: [{ column: "display_name" }],
    });
    if (resp.error) throw resp.error;

    return resp.data;
  }, []);

  return (
    <>
      <Box display="flex" justifyContent="flex-start" pb={4}>
        <Button
          data-analytics-id="configure-email-simple"
          variant="text"
          startIcon={<ArrowBackOutlined />}
          onClick={() => setShowAdvancedView(false)}
          size="small"
        >
          Back to email settings
        </Button>
      </Box>
      <Stack direction="column" spacing={1}>
        <Typography variant="h6">Configure email headers</Typography>
        <Typography variant="body2" sx={{ fontSize: 14 }} gutterBottom>
          Add custom headers. You can use custom headers on email Communications - select “Mark as”
          while editing a Communication to choose custom headers for that message.
        </Typography>
      </Stack>
      {loading && <LinearProgress />}
      {error && <Alert severity="error">Failed to load custom headers</Alert>}
      <Box py={2}>
        <Button
          data-analytics-id="configure-email-advanced-add-header"
          variant="text"
          startIcon={<ControlPointOutlined />}
          size="large"
          onClick={() => customHeaderDialog.handleOpen()}
        >
          Add custom header
        </Button>
      </Box>
      {data && <EmailHeaderTable customHeaders={data} reload={reload} />}
      <UpsertHeaderDialog
        open={customHeaderDialog.open}
        onClose={customHeaderDialog.handleClose}
        onSave={reload}
      />
    </>
  );
};

const SimpleEmailConfiguration: FC<{ setShowAdvancedView: (s: boolean) => void }> = ({
  setShowAdvancedView,
}) => {
  const { ce_admin } = usePermissions();
  const { enable_advanced_email_headers } = useAccountLimitsContext();

  const { data, loading, error, reload } = useSupabaseLoader(async ({ supabase, account_id }) => {
    const supabaseDomainService = new SupabaseCustomEmailDomainService(supabase);
    const resp = await supabaseDomainService.getAll(account_id, {
      order: [{ column: "verified", dir: "desc" }, { column: "created_at" }],
    });
    if (resp.error) throw resp.error;

    return resp.data;
  }, []);

  if (loading) return <LinearProgress />;
  if (error || !data) return <Alert severity="error">Failed to load custom domains</Alert>;

  return (
    <>
      {enable_advanced_email_headers && (
        <Box display="flex" justifyContent="flex-end">
          <Button
            data-analytics-id="configure-email-advanced"
            variant="text"
            startIcon={<SettingsOutlined />}
            onClick={() => setShowAdvancedView(true)}
            size="small"
          >
            Advanced
          </Button>
        </Box>
      )}
      <Stack direction="column">
        <Typography variant="h6">Configure Custom Domains</Typography>
        <Typography component="div" sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
          Configure the domains we’re authorized to send email from
        </Typography>
      </Stack>
      <EmailDomainConfigurationTable customDomains={data} reload={reload} />
      <Divider sx={{ my: 3 }} />
      <Stack direction="column" spacing={0}>
        <Typography variant="h6">Configure Email</Typography>
        <Typography component="div" sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
          Configure the email addresses we’re authorized to send email from
        </Typography>
        {data.length > 0 ? (
          <Suspense fallback={<LinearProgress />}>
            <EmailConfigurationTable customDomains={data.filter((x) => x.verified)} />
          </Suspense>
        ) : ce_admin ? (
          <Alert severity="info">
            <Typography variant="body2">
              You must first configure a custom email domain to add an email address
            </Typography>
          </Alert>
        ) : (
          <Alert severity="info">
            <Typography variant="body2">
              To configure an email address, please contact support to configure a custom domain
            </Typography>
          </Alert>
        )}
      </Stack>
    </>
  );
};
